import React from 'react'

export type VoiceAnimationState = 'idle' | 'listening' | 'processing' | 'speaking'

interface VoiceAnimationProps {
  state: VoiceAnimationState
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const sizeClasses = {
  sm: {
    container: 'tw-w-10 tw-h-10',
    orb: 'tw-w-6 tw-h-6',
    ring1: 'tw-w-8 tw-h-8',
    ring2: 'tw-w-10 tw-h-10',
    ring3: 'tw-w-12 tw-h-12',
  },
  md: {
    container: 'tw-w-16 tw-h-16',
    orb: 'tw-w-10 tw-h-10',
    ring1: 'tw-w-12 tw-h-12',
    ring2: 'tw-w-14 tw-h-14',
    ring3: 'tw-w-16 tw-h-16',
  },
  lg: {
    container: 'tw-w-24 tw-h-24',
    orb: 'tw-w-14 tw-h-14',
    ring1: 'tw-w-18 tw-h-18',
    ring2: 'tw-w-20 tw-h-20',
    ring3: 'tw-w-24 tw-h-24',
  },
}

const VoiceAnimation: React.FC<VoiceAnimationProps> = ({
  state,
  size = 'md',
  className = '',
}) => {
  const sizes = sizeClasses[size]

  if (state === 'idle') {
    return null
  }

  // Different color schemes for different states
  const gradientColors = {
    listening: {
      from: '#3B82F6', // blue-500
      via: '#8B5CF6', // violet-500
      to: '#EC4899', // pink-500
    },
    processing: {
      from: '#F59E0B', // amber-500
      via: '#EF4444', // red-500
      to: '#EC4899', // pink-500
    },
    speaking: {
      from: '#10B981', // emerald-500
      via: '#3B82F6', // blue-500
      to: '#8B5CF6', // violet-500
    },
  }

  const colors = gradientColors[state] || gradientColors.listening

  return (
    <div
      className={`tw-relative tw-flex tw-items-center tw-justify-center ${sizes.container} ${className}`}
    >
      {/* Outer pulsing rings */}
      <div
        className={`tw-absolute tw-rounded-full tw-opacity-30 ${sizes.ring3}`}
        style={{
          background: `radial-gradient(circle, ${colors.from}40, transparent 70%)`,
          animation: 'siri-ring 2s ease-out infinite',
        }}
      />
      <div
        className={`tw-absolute tw-rounded-full tw-opacity-40 ${sizes.ring2}`}
        style={{
          background: `radial-gradient(circle, ${colors.via}50, transparent 70%)`,
          animation: 'siri-ring 2s ease-out infinite 0.4s',
        }}
      />
      <div
        className={`tw-absolute tw-rounded-full tw-opacity-50 ${sizes.ring1}`}
        style={{
          background: `radial-gradient(circle, ${colors.to}60, transparent 70%)`,
          animation: 'siri-ring 2s ease-out infinite 0.8s',
        }}
      />

      {/* Central orb with gradient and glow */}
      <div
        className={`tw-relative tw-rounded-full ${sizes.orb}`}
        style={{
          background: `linear-gradient(135deg, ${colors.from}, ${colors.via}, ${colors.to})`,
          boxShadow: `0 0 20px ${colors.from}80, 0 0 40px ${colors.via}60, 0 0 60px ${colors.to}40`,
          animation: 'siri-pulse 1.5s ease-in-out infinite, siri-color 3s linear infinite',
        }}
      >
        {/* Inner highlight */}
        <div
          className="tw-absolute tw-inset-0 tw-rounded-full tw-opacity-60"
          style={{
            background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4), transparent 60%)',
          }}
        />
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes siri-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.9;
          }
          50% {
            transform: scale(1.15);
            opacity: 1;
          }
        }

        @keyframes siri-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.8;
          }
          100% {
            transform: scale(1.8);
            opacity: 0;
          }
        }

        @keyframes siri-color {
          0%, 100% {
            filter: hue-rotate(0deg);
          }
          50% {
            filter: hue-rotate(30deg);
          }
        }
      `}</style>
    </div>
  )
}

export default VoiceAnimation
